import {Component, FontAwesomeIcon} from 'substance'
import {Event} from '../../../lib/constants/Event'

/**
 * @typedef Component.DialogComponent.Props
 * @property {Component.DialogComponent.Options} options
 * @property {Object} contentProps - Props sent to content component
 * @property {Component} content
 */

/**
 * @typedef Component.DialogComponent.State
 * @property {Boolean} primaryBtnEnabled
 */

/**
 * @typedef Component.DialogComponent.Options
 * @property {string} [title] Dialog title
 * @property {string|boolean} [primary] Primary button caption, default is i18n representation of Ok, set to false to disable button
 * @property {string|boolean} [secondary] Secondary button caption, default is i18n representation of Cancel, set to false to disable button
 * @property {boolean} [focusOnPrimary=true] Set to false to disable default behavior of focusing on primary button. Default true
 * @property {string|Object} [tertiary] Third button
 * @property {boolean} [center] As default the dialog is centered over the editor area, set to true to center over the full writer
 */

/**
 * A dialog component
 * @memberof Component
 *
 * @param {Component.DialogComponent.Props} props
 * @property {Component.DialogComponent.Props} props
 * @property {Component.DialogComponent.State} state
 */
class DialogComponent extends Component {

    constructor(...args) {
        super(...args)

        // When esc:pressed event close the dialog
        this.context.api.events.on('__dialog', Event.USERACTION_KEY_ESCAPE, () => {
            this.props.contentProps.level === 2 ? this.save() : this.close() // If the message is an error, we still want to run the cancelling callback
        })

        // action handlers
        this.handleActions({
            'close': this.close,
            'dialog:enablePrimaryBtn': this.enablePrimaryBtn,
            'dialog:disablePrimaryBtn': this.disablePrimaryBtn
        });
    }

    didMount() {
        window.onresize = () => {
            this.changeModalHeight()
        }

        setTimeout(this.changeModalHeight.bind(this))
        setTimeout(this.focusOnPrimaryBtn.bind(this))
    }

    didUpdate() {
        setTimeout(this.changeModalHeight.bind(this))
    }

    restoreSelection() {
        if (this.previousSelection) {
            try {
                this.context.editorSession.setSelection(this.previousSelection)
            }
            catch(ex) {
                // Previous selection is no longer possible. This happens when
                // an isolated node has destroyed/hidden a field which had the
                // previous selection. First find that node and select it, then
                // the previous selection can be restored.

                const selSurface = this.context.editorSession.getSurface(this.previousSelection.surfaceId)
                let node = selSurface.getParent()

                // Find the isolated node and set it as selection
                while (node) {
                    if (node.parent && node.parent._isSurface) {
                        this.context.editorSession.setSelection({
                            type: 'node',
                            nodeId: node.name,
                            containerId: node.parent.containerId,
                            surfaceId: node.parent._surfaceId
                        })

                        // Now reset the selection to the previous
                        this.context.editorSession.setSelection(this.previousSelection)
                        break
                    }

                    node = node.getParent()
                }
            }
        }
    }

    dispose() {
        this.context.api.events.off('__dialog', Event.USERACTION_KEY_ESCAPE)
        window.onresize = null
    }

    enablePrimaryBtn() {
        this.extendState({
            primaryBtnEnabled: true
        })
    }

    disablePrimaryBtn() {
        this.extendState({
            primaryBtnEnabled: false
        })
    }

    getInitialState() {
        return {
            primaryBtnEnabled: true
        }
    }

    focusOnPrimaryBtn() {
        if (this.refs.primaryButton) {
            if (!this.previousSelection) {
                this.previousSelection = this.context.editorSession.getSelection()
                this.context.editorSession.setSelection(null)
            }

            if(this.shouldFocusPrimaryButton) {
                this.refs.primaryButton.el.focus()
            }
        }
    }

    render($$) {
        /**
         * [
         {
           "type": "info",
           "message": "This is a info message."
         },
         {
           "type": "warning",
           "message": "This is a warning message."
         },
         {
           "type": "error",
           "message": "Artikeln saknar titel vilket kan göra den svår att hitta."
         }
         ]
         */

        const props = this.props;
        const options = props.options;

        const modal = $$('div').attr('id', 'im-modal-default').addClass('modal').ref('modal')

        const modalDialog = $$('div').addClass('modal-dialog').attr('role', 'document').ref('modalDialog')
        const modalContent = $$('div').addClass('modal-content').ref('modalContent')
        const modalBody = $$('div').addClass('modal-body')
            // Make modal respond to enter-click
            .on('keydown', (ev) => {
                if (ev.keyCode === 13 && !ev.shiftKey && document.activeElement.nodeName.toLowerCase() !== 'textarea') {
                    if (this.state.primaryBtnEnabled && this.refs.primaryButton) {
                        this.refs.primaryButton.click()
                        ev.preventDefault()
                        return false;
                    }
                }
            })
            .ref('modalBody')

        const modalFooter = $$('div').addClass('modal-footer').ref('modalFooter')

        const contentProps = props.contentProps
        const contentComponent = props.content;

        if (this.state.saveInProgress) {
            contentProps.saveInProgress = true
        }
        const modalInnerContent = $$(contentComponent, contentProps)
            .addClass('modal-inner-content')
            .ref('contentComponent')

        if (props.options.takeover) {
            modal.addClass('global takeover')
        }
        if (props.options.cssClass) {
            modal.addClass(options.cssClass)
        }

        this.addHeadingIfExist($$, modalBody, options)

        modalBody.append(modalInnerContent)
        this.addTertiaryButtons($$, modalFooter, options)
        this.addPrimaryButtonIfExist($$, modalFooter, options)
        this.addSecondaryButtonIfExist($$, modalFooter, options)
        modalFooter.append($$('div').css({clear: 'both'}))

        this.addTitleIfExist($$, modalContent, options)

        modalContent.append([modalBody, modalFooter])

        modalDialog.append(modalContent)
        modal.append(modalDialog)

        return modal
    }

    /**
     * Private helper function to get labels if labelprovider exists or default to English
     * @private
     *
     * @param {string} label
     * @returns {string}
     */
    _getLabel(label) {
        return this.context.labelProvider ? this.getLabel(label) : label
    }

    /**
     * If a title is provided in the options object it will create a
     * modal-header and append provided title through the label system
     * @private
     *
     * @param $$
     * @param modalContent
     * @param options
     */
    addTitleIfExist($$, modalContent, options) {
        if (options.title) {

            let title = ''
            if (options.icon) {
                title = $$('span').append($$(FontAwesomeIcon, {icon: options.icon})).append(' ' + this._getLabel(options.title))
            } else {
                title = this._getLabel(options.title)
            }
            modalContent.append(
                $$('div').addClass('modal-header').append(
                    $$('h2').append(
                        title
                    )
                )
            );
        }
    }

    /**
     * If a heading is provided in the options object it will create a
     * modal-heading and append provided heading through the label system
     * @private
     *
     * @param $$
     * @param modalBody
     * @param options
     */
    addHeadingIfExist($$, modalBody, options) {
        if (options.heading) {
            let heading = ''
            heading = this._getLabel(options.heading)
            modalBody.append(
                $$('div').addClass('modal-heading').append(
                    $$('h3').append(heading)
                )
            );
        }
    }

    /**
     * Recalculate the height of the modal window depending on the resolution on the client.innerHeight
     */
    changeModalHeight() {
        const clientHeight = window.innerHeight;
        if (clientHeight < 700) {
            this.el.find('.modal-content').addClass('small-screen');
            this.el.find('.modal-content').css('max-height', clientHeight - 20 + "px");
            this.el.find('.modal-content').css('height', 'auto')

            this.el.find('.modal-body').css('max-height', clientHeight - 160 + "px");
            this.el.find('.modal-body').css('height', 'auto')
        }
        else {
            this.el.find('.modal-content').removeClass('small-screen');
            this.el.find('.modal-content').css('max-height', clientHeight - 120 + "px");
            this.el.find('.modal-content').css('height', 'auto')

            this.el.find('.modal-body').css('max-height', clientHeight - 260 + "px");
            this.el.find('.modal-body').css('height', 'auto')
        }
    }

    addPrimaryButtonIfExist($$, modalFooter, options) {
        if (options.primary !== false) {
            modalFooter.append(this.renderPrimaryButton($$, options).ref('primaryButton'));
        }
    }

    addSecondaryButtonIfExist($$, modalFooter, options) {
        if (options.secondary !== false) {
            modalFooter.append(this.renderSecondaryButton($$, options).ref('secondaryButton'));
        }
    }

    addTertiaryButtons($$, modalFooter, options) {
        if (options.tertiary) {
            modalFooter.append(this.renderTertiaryButtons($$, options));
        }
    }

    save() {
        if (this.state.primaryBtnEnabled) {
            this.extendState({
                saveInProgress: true
            })

            if (this.refs.contentComponent.onClose) {
                this.restoreSelection()
                if (this.refs.contentComponent.onClose('save') !== false) {
                    this.send(Event.DIALOG_CLOSE) // Send this close to Writer can reset state
                    this.remove();
                } else {
                    this.focusOnPrimaryBtn()
                    this.extendState({
                        saveInProgress: false
                    })
                }
            }
            else {
                this.extendState({
                    saveInProgress: false
                })
            }
        }
    }

    close() {
        if(this.props.options.disableEscKey) {
            return
        }

        if (this.refs.contentComponent.onClose && this.refs.contentComponent.onClose('cancel') !== false) {
            this.restoreSelection()
            this.send(Event.DIALOG_CLOSE) // Send this close to Writer can reset state
            this.remove();
        }
    }

    renderPrimaryButton($$, options) {
        const level = this.props.contentProps.level;
        const caption = (options.primary) ? options.primary : this._getLabel('ok');

        let button = $$('button')
            .attr('type', 'button')
            .addClass('btn sc-np-btn btn-primary')
            .on('click', this.save);

        if (level === 2) {
            button.addClass('error')
        }
        if(this.state.saveInProgress) {
            button.addClass('disabled');
            button.append($$(FontAwesomeIcon, {icon: 'fa-spinner fa-spin'}))
        } else {
            button.append(caption)
        }
        if (!this.state.primaryBtnEnabled) {
            button.attr('disabled', true)
        }

        return button
    }

    renderSecondaryButton($$, options) {
        var caption = (options.secondary) ? options.secondary : this._getLabel('Cancel');
        return $$('button').attr('type', 'button')
            .addClass('btn sc-np-btn btn-secondary')
            .attr('data-dismiss', 'modal')
            .append(caption)
            .on('click', this.close);
    }

    renderTertiaryButtons($$, options) {
        if (!Array.isArray(options.tertiary)) {
            return $$('span');
        }

        return options.tertiary.map((btn) => {
            return $$('button').attr('type', 'button')
                .addClass('btn sc-np-btn btn-secondary btn-tertiary')
                .attr('data-dismiss', 'modal')
                .append(btn.caption)
                .on('click', () => {
                    var ret = btn.callback();
                    if (ret === true) {
                        this.close();
                    }
                })

        })
    }

    get shouldFocusPrimaryButton() {
        const focusOnPrimary = this.props.options.focusPrimary
        return focusOnPrimary === undefined || Boolean(focusOnPrimary)
    }
}

export default DialogComponent
